import React, { useEffect, useState } from "react";
import { api, showToast } from "@/lib/api";
import { useAuth } from "@/App";

const expBadge = (exp) => {
  if (exp === "positive") return { cls: "eb-good", label: "😊 Positive" };
  if (exp === "negative") return { cls: "eb-bad", label: "😤 Negative" };
  if (exp === "mixed") return { cls: "eb-mixed", label: "🤔 Mixed" };
  return null;
};

const rolePill = (role, isAnon) => {
  if (isAnon) return { cls: "rp-t", label: "Anonymous" };
  if (role === "tenant") return { cls: "rp-tenant", label: "tenant" };
  if (role === "landlord") return { cls: "rp-landlord", label: "landlord" };
  if (role === "both") return { cls: "rp-both", label: "both" };
  return null;
};

// ===================== Share Story Modal =====================
export function ShareStoryModal({ open, onClose, onSubmitted }) {
  const { user, login } = useAuth();
  const [exp, setExp] = useState("");
  const [postAs, setPostAs] = useState("real");
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [city, setCity] = useState("Mumbai");
  const [area, setArea] = useState("");
  const [society, setSociety] = useState("");
  const [tags, setTags] = useState([]);
  const [tagInput, setTagInput] = useState("");
  const [topicChip, setTopicChip] = useState("");
  const [cities, setCities] = useState([]);
  const [topics, setTopics] = useState([]);
  const [images, setImages] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      api.get("/cities").then(r => setCities(r.data || []));
      api.get("/tags").then(r => setTopics(r.data || []));
    }
  }, [open]);

  if (!open) return null;

  const reset = () => {
    setExp(""); setPostAs("real"); setTitle(""); setBody("");
    setCity("Mumbai"); setArea(""); setSociety(""); setTags([]); setTagInput("");
    setTopicChip(""); setImages([]);
  };

  const addTag = () => {
    const t = tagInput.trim();
    if (t && !tags.includes(t)) setTags([...tags, t]);
    setTagInput("");
  };

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    for (const f of files) {
      const fd = new FormData();
      fd.append("file", f);
      try {
        const r = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setImages((prev) => [...prev, r.data.path]);
      } catch (err) {
        showToast(err?.response?.data?.detail || "Upload failed");
      }
    }
    e.target.value = "";
  };

  const submit = async () => {
    if (!user) { login(); return; }
    if (!exp || !title || !body || !city) {
      showToast("Please complete required fields");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/posts", {
        type: "story", experience: exp, title, body,
        city, area, society, tags,
        topic_chip: topicChip || null, post_as: postAs, images
      });
      showToast("Submitted! Awaiting moderator review.");
      reset();
      onClose();
      onSubmitted?.();
    } catch (e) {
      showToast(e?.response?.data?.detail || "Failed to submit");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="mo open" onClick={onClose}>
      <div className="mb wide" onClick={(e)=>e.stopPropagation()}>
        <button className="mclose" onClick={onClose}>✕</button>
        <div className="mtitle">Share Your Story</div>
        <div className="msub">Your experience helps renters and landlords across India.</div>

        <div className="ff">
          <label>How was this experience?</label>
          <div className="exp-picks">
            <div data-testid="exp-positive" className={`ep ${exp==="positive"?"sel-good":""}`} onClick={()=>setExp("positive")}>
              <div className="ep-icon">😊</div><div className="ep-lbl">Positive</div>
            </div>
            <div data-testid="exp-mixed" className={`ep ${exp==="mixed"?"sel-mixed":""}`} onClick={()=>setExp("mixed")}>
              <div className="ep-icon">🤔</div><div className="ep-lbl">Mixed</div>
            </div>
            <div data-testid="exp-negative" className={`ep ${exp==="negative"?"sel-bad":""}`} onClick={()=>setExp("negative")}>
              <div className="ep-icon">😤</div><div className="ep-lbl">Negative</div>
            </div>
          </div>
        </div>

        <div className="ff">
          <label>Post As</label>
          <div className="name-toggle-row">
            <button data-testid="post-as-real" className={`ntr-btn ${postAs==="real"?"on":""}`} onClick={()=>setPostAs("real")}>Real Name</button>
            <button data-testid="post-as-username" className={`ntr-btn ${postAs==="username"?"on":""}`} onClick={()=>setPostAs("username")}>Username</button>
            <button data-testid="post-as-anon" className={`ntr-btn ${postAs==="anonymous"?"on":""}`} onClick={()=>setPostAs("anonymous")}>Anonymous</button>
          </div>
        </div>

        <div className="ff"><label>Title</label>
          <input data-testid="story-title" value={title} onChange={(e)=>setTitle(e.target.value)} placeholder="e.g. Landlord withheld full deposit — Bandra West, Mumbai" />
        </div>

        <div className="g3">
          <div className="ff"><label>City</label>
            <select data-testid="story-city" value={city} onChange={(e)=>setCity(e.target.value)}>
              {cities.map(c => <option key={c.name}>{c.name}</option>)}
            </select>
          </div>
          <div className="ff"><label>Area / Locality</label>
            <input data-testid="story-area" value={area} onChange={(e)=>setArea(e.target.value)} placeholder="Bandra West" />
          </div>
          <div className="ff"><label>Society / Building</label>
            <input data-testid="story-society" value={society} onChange={(e)=>setSociety(e.target.value)} placeholder="Optional" />
          </div>
        </div>

        <div className="ff"><label>Topic Tag</label>
          <select data-testid="story-topic" value={topicChip} onChange={(e)=>setTopicChip(e.target.value)}>
            <option value="">— Select topic —</option>
            {topics.map(t => <option key={t.name}>{t.name}</option>)}
          </select>
        </div>

        <div className="ff"><label>Your Story</label>
          <textarea data-testid="story-body" style={{minHeight:120}} value={body} onChange={(e)=>setBody(e.target.value)} placeholder="Describe your experience in detail. What happened? What should others know?" />
        </div>

        <div className="ff">
          <label>Tags <span style={{fontWeight:400,textTransform:"none",letterSpacing:0,fontSize:11,color:"#AEAEB2"}}>Type and press Enter to add custom tags</span></label>
          <div className="tag-input-wrap" onClick={(e)=>e.currentTarget.querySelector("input").focus()}>
            {tags.map(t => (
              <span key={t} className="ti-tag">{t}<button className="ti-tag-x" onClick={()=>setTags(tags.filter(x=>x!==t))}>×</button></span>
            ))}
            <input className="ti-input" data-testid="story-tag-input" value={tagInput}
                   onChange={(e)=>setTagInput(e.target.value)}
                   onKeyDown={(e)=>{ if(e.key==="Enter"){ e.preventDefault(); addTag(); } }}
                   placeholder="Add tag…" />
          </div>
        </div>

        <div className="ff"><label>Photos (optional)</label>
          <label className="img-uploader" htmlFor="img-up">📷 Click to upload images (jpg/png/webp, up to 8MB each)</label>
          <input id="img-up" type="file" accept="image/*" multiple style={{display:"none"}} onChange={handleUpload} data-testid="story-image-upload" />
          {images.length > 0 && (
            <div className="img-preview-grid">
              {images.map((p, i) => (
                <div className="img-preview" key={p}>
                  <img src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${p}`} alt="" />
                  <button className="ipx" onClick={()=>setImages(images.filter((_,idx)=>idx!==i))}>×</button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="warn-note">⏳ Posts are reviewed before going live. Honest, detailed posts typically approved within 1–2 hours.</div>
        <button className="btn-full" data-testid="story-submit-btn" disabled={submitting} onClick={submit}>
          {submitting ? "Submitting…" : "Submit for Review →"}
        </button>
      </div>
    </div>
  );
}

// ===================== Start Topic Modal =====================
export function StartTopicModal({ open, onClose, onSubmitted }) {
  const { user, login } = useAuth();
  const [tt, setTT] = useState("");
  const [postAs, setPostAs] = useState("real");
  const [title, setTitle] = useState("");
  const [city, setCity] = useState("General");
  const [cat, setCat] = useState("Deposit Disputes");
  const [customCat, setCustomCat] = useState("");
  const [body, setBody] = useState("");
  const [cities, setCities] = useState([]);
  const [topics, setTopics] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      api.get("/cities").then(r => setCities(r.data || []));
      api.get("/tags").then(r => setTopics(r.data || []));
    }
  }, [open]);

  if (!open) return null;

  const submit = async () => {
    if (!user) { login(); return; }
    if (!title || !body) { showToast("Please complete required fields"); return; }
    if (cat === "Other" && !customCat.trim()) { showToast("Please type a custom topic name"); return; }
    setSubmitting(true);
    const finalChip = cat === "Other" ? customCat.trim() : cat;
    try {
      await api.post("/posts", {
        type: "topic", topic_type: tt || "discussion",
        title, body, city: city === "General" ? "All Cities" : city,
        area: "", society: "", tags: [],
        topic_chip: finalChip, post_as: postAs, images: []
      });
      showToast("Topic submitted for review");
      setTT(""); setTitle(""); setBody(""); setCustomCat("");
      onClose();
      onSubmitted?.();
    } catch (e) {
      showToast(e?.response?.data?.detail || "Failed to submit");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="mo open" onClick={onClose}>
      <div className="mb wide" onClick={(e)=>e.stopPropagation()}>
        <button className="mclose" onClick={onClose}>✕</button>
        <div className="mtitle">Start a Topic</div>
        <div className="msub">Start a focused discussion thread for the community to join in.</div>

        <div className="ff"><label>Topic Type</label>
          <div className="topic-type-picks">
            {[["ask","❓","Ask the Community"],["advice","💡","Share Advice"],["discussion","📊","Start a Discussion"],["alert","⚠️","Community Alert"]].map(([k,i,l])=>(
              <div key={k} data-testid={`topic-type-${k}`} className={`ttp ${tt===k?"on":""}`} onClick={()=>setTT(k)}>
                <div className="ttp-icon">{i}</div><div className="ttp-lbl">{l}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="ff"><label>Post As</label>
          <div className="name-toggle-row">
            <button className={`ntr-btn ${postAs==="real"?"on":""}`} onClick={()=>setPostAs("real")}>Real Name</button>
            <button className={`ntr-btn ${postAs==="username"?"on":""}`} onClick={()=>setPostAs("username")}>Username</button>
            <button className={`ntr-btn ${postAs==="anonymous"?"on":""}`} onClick={()=>setPostAs("anonymous")}>Anonymous</button>
          </div>
        </div>

        <div className="ff"><label>Topic Title</label>
          <input data-testid="topic-title" value={title} onChange={(e)=>setTitle(e.target.value)} placeholder="e.g. Is 6 months security deposit legal in Hyderabad?" />
        </div>

        <div className="g2">
          <div className="ff"><label>City</label>
            <select value={city} onChange={(e)=>setCity(e.target.value)}>
              <option value="General">General (All Cities)</option>
              {cities.map(c => <option key={c.name}>{c.name}</option>)}
            </select>
          </div>
          <div className="ff"><label>Topic Category</label>
            <select value={cat} onChange={(e)=>setCat(e.target.value)}>
              {topics.map(t => <option key={t.name}>{t.name}</option>)}
              <option value="Other">Other</option>
            </select>
            {cat === "Other" && (
              <input
                data-testid="topic-custom-cat"
                value={customCat}
                onChange={(e)=>setCustomCat(e.target.value)}
                placeholder="Type your custom topic name…"
                style={{marginTop:8}}
              />
            )}
          </div>
        </div>

        <div className="ff"><label>Description</label>
          <textarea data-testid="topic-body" style={{minHeight:100}} value={body} onChange={(e)=>setBody(e.target.value)} placeholder="Provide context for the discussion. What's the situation? What would you like people to share?" />
        </div>

        <div className="warn-note">⏳ Topics are reviewed before going live.</div>
        <button data-testid="topic-submit-btn" className="btn-full" disabled={submitting} onClick={submit}>
          {submitting ? "Submitting…" : "Start Topic →"}
        </button>
      </div>
    </div>
  );
}

// ===================== Add City Modal =====================
const SUGGESTED_CITIES = ["Ahmedabad","Jaipur","Surat","Lucknow","Chandigarh","Kochi","Indore","Goa","Coimbatore","Visakhapatnam"];

export function AddCityModal({ open, onClose, onAdded }) {
  const { user, login } = useAuth();
  const [name, setName] = useState("");
  const [pending, setPending] = useState([]);

  useEffect(() => {
    if (open) {
      api.get("/cities").then(() => {});
    }
  }, [open]);

  if (!open) return null;

  const submit = async () => {
    if (!user) { login(); return; }
    const finalName = name.trim();
    if (!finalName) { showToast("Type a city name"); return; }
    try {
      await api.post("/cities", { name: finalName });
      setPending([...pending, finalName]);
      setName("");
      showToast("City suggestion sent for review");
      onAdded?.();
    } catch (e) {
      showToast(e?.response?.data?.detail || "Failed");
    }
  };

  return (
    <div className="mo open" onClick={onClose}>
      <div className="mb" style={{maxWidth:440}} onClick={(e)=>e.stopPropagation()}>
        <button className="mclose" onClick={onClose}>✕</button>
        <div className="mtitle">Add a City</div>
        <div className="msub">Suggest a new city to be added to the forum. It will appear after moderator review.</div>
        <div className="ff"><label>Type City Name</label>
          <input data-testid="add-city-input" value={name} onChange={(e)=>setName(e.target.value)} placeholder="e.g. Ahmedabad, Jaipur, Surat…" />
        </div>
        <div className="ff"><label>Or Select from Suggestions</label>
          <div className="suggested-cities">
            {SUGGESTED_CITIES.map(c => (
              <div key={c} className={`sc-pill ${name===c?"on":""}`} onClick={()=>setName(c)}>{c}</div>
            ))}
          </div>
        </div>
        {pending.length > 0 && <div className="warn-note">Pending review: {pending.join(", ")}</div>}
        <button className="btn-full" data-testid="add-city-submit" onClick={submit}>Add City →</button>
      </div>
    </div>
  );
}

// ===================== Add Tag Modal =====================
export function AddTagModal({ open, onClose, onAdded }) {
  const { user, login } = useAuth();
  const [name, setName] = useState("");
  const [reason, setReason] = useState("");

  if (!open) return null;

  const submit = async () => {
    if (!user) { login(); return; }
    if (!name.trim()) { showToast("Type a tag name"); return; }
    try {
      await api.post("/tags", { name: name.trim(), reason });
      showToast("Tag suggestion sent for review");
      setName(""); setReason("");
      onClose();
      onAdded?.();
    } catch (e) {
      showToast(e?.response?.data?.detail || "Failed");
    }
  };

  return (
    <div className="mo open" onClick={onClose}>
      <div className="mb" style={{maxWidth:440}} onClick={(e)=>e.stopPropagation()}>
        <button className="mclose" onClick={onClose}>✕</button>
        <div className="mtitle">Create Topic Tag</div>
        <div className="msub">Add a new topic category to help organise discussions on the forum.</div>
        <div className="ff"><label>Tag Name</label>
          <input data-testid="add-tag-input" value={name} onChange={(e)=>setName(e.target.value)} placeholder="e.g. Pet-Friendly, Women Safety, Water Issues…" />
        </div>
        <div className="ff"><label>Why is this tag needed?</label>
          <textarea value={reason} onChange={(e)=>setReason(e.target.value)} style={{minHeight:70}} placeholder="Briefly explain why this topic tag would help the community…" />
        </div>
        <button className="btn-full" data-testid="add-tag-submit" onClick={submit}>Submit Tag →</button>
      </div>
    </div>
  );
}

// helpers exported
export { expBadge, rolePill };
